import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CheckCircle } from '@mui/icons-material';
import api from '../../services/api';
import { normalizeHex } from '../Admin/ColorPickerPopover';
import { useI18n } from '../../i18n/useI18n';

/** Preset giống mlite AppThemeColorWindow */
export const APP_ACCENT_PRESETS = [
  '#d45d7f', // pink (default)
  '#26A69A', // teal
  '#5C6BC0', // indigo
  '#EF6C00', // orange
  '#7B1FA2', // purple
  '#00897B', // green-teal
  '#3949AB', // blue
  '#C2185B', // magenta
  '#212121', // black
];

/** Chỉ chấp nhận #RGB / #RRGGBB — khác normalizeHex (fallback #000). */
function tryParseAccentHex(raw: string): string | null {
  if (!raw || typeof raw !== 'string') return null;
  let t = raw.trim();
  if (!t.startsWith('#')) t = `#${t}`;
  if (/^#[0-9a-fA-F]{6}$/.test(t)) return t.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(t)) {
    const r = t[1];
    const g = t[2];
    const b = t[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return null;
}

type AppChromePalette = {
  bg: string;
  surface: string;
  border: string;
  soft: string;
  primary: string;
  hover: string;
  text: string;
};

function clamp01(n: number) {
  return Math.min(1, Math.max(0, n));
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = normalizeHex(hex).slice(1);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  const to = (v: number) => Math.round(clamp01(v) * 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (Math.abs(max - min) < 0.0001) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h, s, l };
}

function hueToRgb(p: number, q: number, t: number) {
  let tt = t;
  if (tt < 0) tt += 1;
  if (tt > 1) tt -= 1;
  if (tt < 1 / 6) return p + (q - p) * 6 * tt;
  if (tt < 1 / 2) return q;
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
  return p;
}

function fromHsl(h: number, s: number, l: number): string {
  const ss = clamp01(s);
  const ll = clamp01(l);
  if (ss <= 0.0001) return rgbToHex(ll, ll, ll);
  const q = ll < 0.5 ? ll * (1 + ss) : ll + ss - ll * ss;
  const p = 2 * ll - q;
  return rgbToHex(
    hueToRgb(p, q, h + 1 / 3),
    hueToRgb(p, q, h),
    hueToRgb(p, q, h - 1 / 3),
  );
}

/** Giống mlite AppChromeThemeService.DerivePalette */
export function deriveAppChromePalette(primaryHex: string): AppChromePalette {
  const { r, g, b } = hexToRgb(primaryHex);
  const { h, s, l } = rgbToHsl(r, g, b);
  return {
    bg: fromHsl(h, Math.min(s * 0.35, 0.45), 0.97),
    surface: fromHsl(h, Math.min(s * 0.55, 0.55), 0.95),
    border: fromHsl(h, Math.min(s * 0.75, 0.65), 0.8),
    soft: fromHsl(h, Math.min(s * 0.65, 0.55), 0.86),
    primary: normalizeHex(primaryHex),
    hover: fromHsl(h, Math.min(s * 1.05, 0.75), Math.max(0.12, l - 0.08)),
    text: fromHsl(h, Math.min(s * 0.55, 0.45), Math.max(0.18, l - 0.22)),
  };
}

const PALETTE_SLOT_KEYS: (keyof AppChromePalette)[] = [
  'bg',
  'surface',
  'border',
  'soft',
  'primary',
  'hover',
  'text',
];

type AppAccentColorAppsProps = {
  /** Admin: đổi màu tài khoản của user này */
  userId?: string;
  /** Đổi màu đúng 1 máy — ưu tiên hơn bulk account */
  machineCode?: string;
};

const AppAccentColorApps: React.FC<AppAccentColorAppsProps> = ({ userId, machineCode }) => {
  const { pages } = useI18n();
  const t = pages.colorApps;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState('#d45d7f');
  const [hexDraft, setHexDraft] = useState('#d45d7f');
  const [hexError, setHexError] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const mc = (machineCode || '').trim().toUpperCase();
  const isMachineScope = Boolean(mc);

  const endpoint = useMemo(() => {
    if (isMachineScope) {
      return userId
        ? `/admin/users/${userId}/machines/${mc}/app-accent-color`
        : `/machines/${mc}/app-accent-color`;
    }
    return userId ? `/admin/users/${userId}/app-accent-color` : '/themes/app-accent-color';
  }, [userId, mc, isMachineScope]);

  const palette = useMemo(() => deriveAppChromePalette(selected), [selected]);

  const setAccentLocal = useCallback((hex: string) => {
    const n = normalizeHex(hex);
    setSelected(n);
    setHexDraft(n);
    setHexError(null);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(endpoint);
      const hex = normalizeHex(res.data?.appAccentColorHex || '#d45d7f');
      setAccentLocal(hex);
    } catch (e: any) {
      setSnack({
        open: true,
        message: e?.response?.data?.message || t.loadError,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [endpoint, setAccentLocal, t.loadError]);

  useEffect(() => {
    load();
  }, [load]);

  const applyColor = async (hex: string) => {
    const parsed = tryParseAccentHex(hex);
    if (!parsed) {
      setHexError(t.invalidHex);
      return;
    }
    setAccentLocal(parsed);
    setSaving(true);
    try {
      await api.patch(endpoint, {
        appAccentColorHex: parsed,
        source: userId ? 'admin' : 'web',
      });
      setSnack({
        open: true,
        message: isMachineScope ? t.saveSuccessMachine : t.saveSuccessAll,
        severity: 'success',
      });
    } catch (e: any) {
      setSnack({
        open: true,
        message: e?.response?.data?.message || t.saveError,
        severity: 'error',
      });
      await load();
    } finally {
      setSaving(false);
    }
  };

  const onHexDraftChange = (raw: string) => {
    setHexDraft(raw);
    const parsed = tryParseAccentHex(raw);
    if (parsed) {
      setHexError(null);
      setSelected(parsed); // preview live như mlite, chưa lưu API
    } else if (raw.trim()) {
      setHexError(t.invalidHex);
    } else {
      setHexError(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={36} sx={{ color: '#0f3460' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.divider, 0.8),
          mb: 2,
          bgcolor: palette.bg,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: palette.text, mb: 0.5 }}>
          {isMachineScope ? t.titleMachine : t.titleAll}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2.5, color: palette.text, opacity: 0.75 }}>
          {isMachineScope ? t.descriptionMachine : t.descriptionAll}
        </Typography>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.text, mb: 1 }}>
          {t.hexLabel}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 1,
            alignItems: 'center',
            mb: hexError ? 0.5 : 2.5,
            opacity: saving ? 0.7 : 1,
          }}
        >
          <Box
            component="label"
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: tryParseAccentHex(hexDraft) || selected,
              border: '2px solid',
              borderColor: palette.border,
              cursor: 'pointer',
              overflow: 'hidden',
              flexShrink: 0,
              position: 'relative',
            }}
            title={t.pickColor}
          >
            <Box
              component="input"
              type="color"
              value={tryParseAccentHex(hexDraft) || selected}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const v = normalizeHex(e.target.value);
                setHexDraft(v);
                setHexError(null);
                setSelected(v);
              }}
              sx={{
                position: 'absolute',
                inset: 0,
                opacity: 0,
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                border: 0,
                p: 0,
              }}
            />
          </Box>

          <TextField
            size="small"
            value={hexDraft}
            onChange={(e) => onHexDraftChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyColor(hexDraft);
              }
            }}
            placeholder="#d45d7f"
            error={Boolean(hexError)}
            disabled={saving}
            sx={{
              flex: '1 1 auto',
              minWidth: 0,
              maxWidth: 140,
              '& .MuiOutlinedInput-root': {
                height: 44,
                bgcolor: palette.surface,
                fontFamily: 'monospace',
                fontWeight: 600,
                fontSize: '0.9rem',
              },
              '& .MuiOutlinedInput-input': {
                py: 0,
                height: 44,
                boxSizing: 'border-box',
              },
            }}
            inputProps={{ maxLength: 7, spellCheck: false }}
          />

          <Button
            variant="contained"
            disabled={saving || Boolean(hexError) || !tryParseAccentHex(hexDraft)}
            onClick={() => applyColor(hexDraft)}
            sx={{
              height: 44,
              minWidth: 0,
              flexShrink: 0,
              px: 1.75,
              fontWeight: 700,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              bgcolor: palette.primary,
              color: '#fff',
              '&:hover': { bgcolor: palette.hover },
              '&.Mui-disabled': { bgcolor: alpha(palette.primary, 0.4), color: '#fff' },
            }}
          >
            {t.apply}
          </Button>
        </Box>
        {hexError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: 'block', mb: 2, mt: 0.25 }}
          >
            {hexError}
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
            gap: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.text }}>
            {t.presets}
          </Typography>
          {saving && <CircularProgress size={18} sx={{ color: palette.primary }} />}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 1.25,
            overflowX: 'auto',
            overflowY: 'hidden',
            pb: 1,
            mx: { xs: -0.5, sm: 0 },
            px: { xs: 0.5, sm: 0 },
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            opacity: saving ? 0.7 : 1,
            pointerEvents: saving ? 'none' : 'auto',
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: alpha(palette.text, 0.25),
              borderRadius: 999,
            },
          }}
        >
          {APP_ACCENT_PRESETS.map((hex) => {
            const active = normalizeHex(selected) === normalizeHex(hex);
            const p = deriveAppChromePalette(hex);
            return (
              <Box
                key={hex}
                onClick={() => applyColor(hex)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    applyColor(hex);
                  }
                }}
                title={hex}
                sx={{
                  flex: '0 0 auto',
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: '3px solid',
                  borderColor: active ? palette.text : alpha(palette.text, 0.2),
                  boxShadow: active ? `0 0 0 2px ${alpha(palette.primary, 0.35)}` : 'none',
                  position: 'relative',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  '&:hover': { transform: 'scale(1.05)' },
                }}
              >
                <Box sx={{ height: '42%', bgcolor: p.primary }} />
                <Box
                  sx={{
                    height: '58%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box sx={{ flex: 1, bgcolor: p.soft }} />
                  <Box sx={{ flex: 1, bgcolor: p.surface }} />
                  <Box sx={{ flex: 1, bgcolor: p.bg }} />
                </Box>
                {active && (
                  <CheckCircle
                    sx={{
                      position: 'absolute',
                      right: -6,
                      top: -6,
                      fontSize: 22,
                      color: palette.text,
                      bgcolor: '#fff',
                      borderRadius: '50%',
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1.5, fontWeight: 700, color: palette.text }}>
          {t.derivedPalette}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: { xs: 1, sm: 1.25 },
          }}
        >
          {PALETTE_SLOT_KEYS.map((key) => (
            <Box key={key} sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  height: { xs: 36, sm: 44 },
                  borderRadius: 1.5,
                  bgcolor: palette[key],
                  border: '1px solid',
                  borderColor: alpha(palette.text, 0.18),
                  mb: 0.5,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: palette.text,
                  display: 'block',
                  fontSize: { xs: '0.68rem', sm: '0.75rem' },
                  lineHeight: 1.2,
                }}
              >
                {t.slots[key]}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: { xs: 9, sm: 10 },
                  color: palette.text,
                  opacity: 0.65,
                }}
              >
                {palette[key]}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            mt: 3,
            p: { xs: 1.5, sm: 2 },
            borderRadius: 2,
            bgcolor: palette.surface,
            border: '1px solid',
            borderColor: palette.border,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, color: palette.text, mb: 1 }}>
            {t.preview}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: { xs: 0.75, sm: 1.25 },
              alignItems: 'center',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Box
              sx={{
                flex: '0 0 auto',
                px: { xs: 1.25, sm: 2 },
                py: { xs: 0.75, sm: 1 },
                borderRadius: 2,
                bgcolor: palette.primary,
                color: '#fff',
                fontWeight: 700,
                fontSize: { xs: 12, sm: 13 },
                whiteSpace: 'nowrap',
              }}
            >
              {t.primaryBtn}
            </Box>
            <Box
              sx={{
                flex: '0 0 auto',
                px: { xs: 1.25, sm: 2 },
                py: { xs: 0.75, sm: 1 },
                borderRadius: 2,
                bgcolor: palette.soft,
                color: palette.text,
                border: '1px solid',
                borderColor: palette.border,
                fontWeight: 600,
                fontSize: { xs: 12, sm: 13 },
                whiteSpace: 'nowrap',
              }}
            >
              {t.secondaryBtn}
            </Box>
            <Box
              sx={{
                flex: '0 0 auto',
                px: { xs: 1.1, sm: 1.5 },
                py: { xs: 0.55, sm: 0.75 },
                borderRadius: 999,
                bgcolor: palette.bg,
                color: palette.primary,
                border: '1px solid',
                borderColor: palette.border,
                fontSize: { xs: 11, sm: 12 },
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {t.badgeChip}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={2800}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppAccentColorApps;
